import {
    useEligibleForJiraMadlibSetup,
    useEligibleForTwcg1935CodingAgentChoice,
    useIsEligibleForTWCAILedOnboarding,
    useIsDesktop,
    useLoggedIn,
    useLoggedOut,
    useLoggedInHasJiraSiteD1toD14,
    useLoggedInHasJSM,
    useNewUser,
    usePrefersDarkColorScheme,
    useSpecifiedPathnames,
    useIsSoftwareConfluencePath,
    useIsSoftwareJiraPath,
    useLoggedOutJira
} from "@/components/user-context/helpers/cohorts";
import { useLocation } from "@/utils/location-provider";

import { useEnglishLocale } from "../sentry-intl";

import { Manifest } from "./use-experiment";
import {
    wacRefreshConfluence,
    wacRefreshHomepage,
    wacRefreshJira,
    wacRefreshTwcRovo
} from "./wac-refresh-experiments";

const booleanTypeGuard = (value: unknown): value is boolean =>
    typeof value === "boolean";

export const experiments = {
    gwp9973JSMBackToProductHero: {
        experimentName: "jsm_back_to_product_hero",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // oxlint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedInHasJSM() && useIsDesktop();
            }
        },
        metadata: {
            description:
                "Back to product hero on JSM product page for logged-in users with a JSM site",
            expectedReleaseDate: "04/16/2026",
            expectedRuntime: "10 weeks",
            context: {
                urls: ["/software/jira/service-management"]
            }
        }
    },
    gwp9322WacHomepageRefresh: {
        experimentName: "gwp-9322-wac-homepage-refresh-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Teamwork Collection refresh changes on homepage",
            expectedReleaseDate: "01/15/2026",
            expectedRuntime: "indefinite",
            context: {
                urls: ["/"]
            }
        }
    },
    cfind4782LandingPageSubAnchorV3: {
        experimentName: "cfind-4782-landing-page-sub-anchor-v3",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Test if personalizing the landing page off the sub anchor will increase d1to6",
            expectedReleaseDate: "07/14/2025",
            expectedRuntime: "6 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jiraOnboardingAiMadlibV2: {
        experimentName: "jira-onboarding-ai-madlib-v2",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useEligibleForJiraMadlibSetup
        },
        metadata: {
            description:
                "Enable AI Madlib during Jira onboarding experience V2 expanded iteration with all team types",
            expectedReleaseDate: "2/27/2026",
            expectedRuntime: "30 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfindSimplifiedListViewWithBasicTasks: {
        experimentName: "jfind-simplified-list-view-with-basic-tasks",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Simplifies the list view columns and adds basic sample tasks to the initial project",
            expectedReleaseDate: "17/06/2025",
            expectedRuntime: "131 days",
            context: {
                urls: []
            }
        }
    },
    jfindBasicSampleTasksForScrumOrSoftwareKanban: {
        experimentName: "jira-create-generic-tasks-and-minimize-quickstart",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Adds basic sample tasks to the initial project for Scrum or Software Kanban projects",
            expectedReleaseDate: "22/09/2025",
            expectedRuntime: "37 days",
            context: {
                urls: []
            }
        }
    },
    signupAddPaymentAb: {
        experimentName: "signup_trialing_add_payment_ab",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "test",
        options: {
            // we need to fire our exposure event manually because we can't determine if the user is eligible from here
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Experiment to enable users with valid sites to go to payment at the sign-up screen",
            expectedReleaseDate: "02/27/25",
            expectedRuntime: "TBD",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp5090ExperimentBannerConfluence: {
        experimentName: "gwp-5090-experiment-banner-confluence",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "butterbar" | "lifted",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "butterbar" | "lifted" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "butterbar", "lifted"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Testing butterbar vs lifted banner for Jira Service Management product tour",
            expectedReleaseDate: "02/18/25",
            expectedRuntime: "2 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp7617JiraShortTermAiInfusionExperiment: {
        experimentName: "gwp-7617-jira-short-term-ai-infusion-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Short term AI",
            expectedReleaseDate: "08/21/25",
            expectedRuntime: "indefinite",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    jfindMatureExampleProjectExperiment: {
        experimentName: "jfind-mature-example-project",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useNewUser
        },
        metadata: {
            description:
                "Supports creating a new example project for Jira customers",
            expectedReleaseDate: "06/02/2025",
            expectedRuntime: "13 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfindMatureExampleProjectV2Experiment: {
        experimentName: "jfind-mature-example-project-v2",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Creates a V2 Mature Example Project for Jira customers",
            expectedReleaseDate: "03/23/2026",
            expectedRuntime: "24 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    gwp4948GartnerJiraProductTourExperiment: {
        experimentName: "gwp-4948-gartner-jira-product-tour-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "gartner section on jira product tour",
            expectedReleaseDate: "April 2025",
            expectedRuntime: "2 weeks",
            clientOnly: true,
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    gwp6441ConfluenceTemplatesProductNavCta: {
        experimentName: "gwp-6441-confluence-templates-product-nav-cta",
        parameterName: "variant",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // oxlint-disable-next-line react-hooks/rules-of-hooks
                return !useLoggedOut();
            }
        },
        metadata: {
            description: "Confluence Templates Product Nav CTA experiment",
            expectedReleaseDate: "undefined",
            expectedRuntime: "77 days",
            clientOnly: true,
            context: {
                urls: ["/software/confluence/templates/*"]
            }
        }
    },
    jiraOnboardingAutodetectSystemTheme: {
        experimentName: "jira-onboarding-autodetect-system-theme",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: usePrefersDarkColorScheme
        },
        metadata: {
            description:
                "Autodetect system theme and enroll users into experiment",
            expectedReleaseDate: "08/27/2025",
            expectedRuntime: "18 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfindOnboardingWizardInviteStep: {
        experimentName: "jfind-add-invite-step-to-onboarding-wizard",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Add invite step to the Jira onboarding wizard",
            expectedReleaseDate: "09/12/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    gwpAJContextualBanditsJiraBPS: {
        experimentName: "gwp-aj-contextual-bandits-jira-bps-v3",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // oxlint-disable-next-line react-hooks/rules-of-hooks
                const location = useLocation();
                if (
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useSpecifiedPathnames(["/software/jira"]) &&
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useLoggedOut() &&
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useEnglishLocale() &&
                    location.search.includes("keyword") &&
                    (typeof window !== "undefined"
                        ? (window as Window & { anchor?: string }).anchor ===
                          "Brand (default)"
                        : location.search.includes("anchor=Brand%20(default)"))
                ) {
                    return true;
                }
                return false;
            }
        },
        metadata: {
            description: "Contextual bandits for Jira BPS v2",
            expectedReleaseDate: "02/13/2026",
            expectedRuntime: "11 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },

    wacRoutingTableMvpPerf: {
        experimentName: "wac-routing-table-mvp",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Measure performance degredation of wac-routing-table in wac-web",
            expectedReleaseDate: "08/28/2025",
            expectedRuntime: "5 days",
            context: {
                urls: ["/software/jira/enterprise"]
            }
        }
    },
    confluencePricingPageWaeMigration: {
        experimentName: "confluence_pricing_wae_migration_exp_rerun_exp_1",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Migration of Confluence pricing page to WAE, rerun",
            expectedReleaseDate: "03/06/2026",
            expectedRuntime: "80 days",
            context: {
                urls: ["/software/confluence/pricing"]
            }
        }
    },
    wacAISearchSummaryV2: {
        experimentName: "wac-ai-search-summary-v2",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // oxlint-disable-next-line react-hooks/rules-of-hooks
                return useIsDesktop();
            }
        },
        metadata: {
            description: "AI Search Summary Experiment V2",
            expectedReleaseDate: "N/A",
            expectedRuntime: "N/A",
            context: {
                // eslint-disable-next-line local/warn-wildcard-usage-eslint
                urls: ["*"]
            }
        }
    },
    gwp8288PersonalizingWacSoftwarePage: {
        experimentName: "gwp-8288-personalizing-wac-software-page",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Personalizing WAC Software Page Experiment",
            expectedReleaseDate: "10/30/2025",
            expectedRuntime: "6 weeks",
            context: {
                urls: ["/software"]
            }
        }
    },
    twcg790ContextualBackground: {
        experimentName: "twcg-790-contextual-backgrounds-wac-sign-in",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "adding atlOrigin and application query strings to IDAC login URL to enable contextual background on sign in page",
            expectedReleaseDate: "01/12/2025",
            expectedRuntime: "7 weeks",
            clientOnly: true,
            context: {
                // eslint-disable-next-line
                urls: ["*"]
            }
        }
    },
    jiraPricingWaeMigrationExperimentRerunExp: {
        experimentName: "jira_pricing_wae_migration_experiment_rerun_exp_1",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Rerun of migration of Jira pricing page to WAE",
            expectedReleaseDate: "03/06/2026",
            expectedRuntime: "43 days",
            context: {
                urls: ["/software/jira/pricing"]
            }
        }
    },
    dxpMigrationConfluenceAnchorJourney: {
        experimentName: "dxp_migration_confluence_anchor_journey",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Migration of Confluence anchor journey to DXP",
            expectedReleaseDate: "TBD",
            expectedRuntime: "TBD",
            context: {
                urls: ["/software/confluence"]
            }
        }
    },
    gwpAJContextualBanditsConfluenceSubAnchor: {
        experimentName: "gwp-aj-contextual-bandits-confluence-subanchor",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                if (
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useSpecifiedPathnames(["/software/confluence"]) &&
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useLoggedOut() &&
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useEnglishLocale()
                ) {
                    return true;
                }
                return false;
            }
        },
        metadata: {
            description: "Contextual bandits for Confluence sub anchor",
            expectedReleaseDate: "12/19/2025",
            expectedRuntime: "12 weeks",
            context: {
                urls: ["/software/confluence"]
            }
        }
    },
    gwp9508ConfluenceBannerIosNativeApp: {
        experimentName: "gwp-9508-confluence-banner-ios-native-app",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useIsSoftwareConfluencePath
        },
        metadata: {
            description: "Confluence Banner Ios Native App Experiment",
            expectedReleaseDate: "01/22/2026",
            expectedRuntime: "39 days",
            context: {
                urls: ["/software/confluence/*"]
            }
        }
    },
    gwp9507JiraBannerIosNativeApp: {
        experimentName: "gwp-9507-jira-banner-ios-native-app",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useIsSoftwareJiraPath
        },
        metadata: {
            description: "Jira Banner App Experiment",
            expectedReleaseDate: "01/22/2026",
            expectedRuntime: "60 days",
            context: {
                urls: ["/software/jira/*"]
            }
        }
    },
    cfind6469FrictionlessFirstLookJiraV3: {
        experimentName: "cfind-6469-frictionless-first-look-jira-v3",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "v3 of the Jira frictionless first look: Update the prompt box to be more intuitive",
            expectedReleaseDate: "06/18/2026",
            expectedRuntime: "",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    d1d6DeferSiteNaming: {
        experimentName: "d1d6-defer-site-naming",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedOutJira
        },
        metadata: {
            description:
                "Remove the explicit site naming step from the Jira signup flow. In treatment, the continueUrl passes deferSiteNaming=true to the confirmation page, which auto-generates the site name and activates the product without a separate site naming step.",
            expectedReleaseDate: "TBD",
            expectedRuntime: "TBD",
            context: {
                urls: ["/software/jira", "/try/cloud/signup"]
            }
        }
    },
    jiraWacActivationChecklist: {
        experimentName: "wac-jira-activation-checklist",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedInHasJiraSiteD1toD14
        },
        metadata: {
            description:
                "Per-product activation checklist surfaced on wac.com/software/jira, rendered directly below the user's most recently accessed Jira site card. Hosts the @post-office/placements--growth-starburst/wac-product-activation-checklist placement.",
            expectedReleaseDate: "06/01/2026",
            expectedRuntime: "4 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    cfind6099JiraRemoveSocialSignups: {
        experimentName: "cfind-6099-jira-remove-social-signups",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedOutJira
        },
        metadata: {
            description:
                "On Jira try/cloud/signup for logged-out users, show Google social signup only (hide Microsoft, Apple, Slack).",
            expectedReleaseDate: "04/01/2026",
            expectedRuntime: "TBD",
            context: {
                urls: ["/try/cloud/signup"]
            }
        }
    },
    gwp10724JiraAgentHeroChangesOnJiraPt: {
        experimentName: "gwp-10724-jira-agent-hero-changes-on-jira-pt",
        parameterName: "isEnabled",
        defaultValue: true,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Post-rollout retroactive experiment for visual changes above the fold on the Jira PT, related to Jira Agent release",
            expectedReleaseDate: "2026-03-18",
            expectedRuntime: "24 days",
            clientOnly: false,
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    productNavGoToAppCta: {
        experimentName: "gwp-10570-product-nav-go-to-jira-cta",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            // oxlint-disable-next-line react-hooks/rules-of-hooks
            cohort: () => useLoggedIn()
        },
        metadata: {
            description: "Jira/Confluence/JSM/JPD Product Nav CTA Experiment",
            expectedReleaseDate: "03/05/2026",
            expectedRuntime: "",
            context: {
                urls: [
                    "/software/jira/*",
                    "/jira/*",
                    "/software/confluence/*",
                    "/software/confluence"
                ]
            }
        }
    },
    gwp10965XflowRecommendation2NbpDriven: {
        experimentName: "xflow_recommendation_2_nbp_driven",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            // oxlint-disable-next-line react-hooks/rules-of-hooks
            cohort: () => useLoggedIn()
        },
        metadata: {
            description:
                "Welcome back header for Confluence, JPD, and Loom crossflow heroes on homepage",
            expectedReleaseDate: "04/10/2026",
            expectedRuntime: "10 weeks",
            context: {
                urls: ["/"]
            }
        }
    },
    twcg947TwcLandAiLedOnboarding: {
        experimentName: "twcg-947_twc_land_ai-led_onboarding",
        parameterName: "cohort",
        defaultValue: "not-enrolled" as
            | "not-enrolled"
            | "control"
            | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: useIsEligibleForTWCAILedOnboarding
        },
        metadata: {
            description: "[TWCG-947] TWC LAND AI-Led Onboarding Experiment",
            expectedReleaseDate: "", // to be updated when known
            expectedRuntime: "", // to be updated when known
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    wacRefreshQ4Fy26AaTest: {
        experimentName: "wac_refresh_q4fy26_aa_test",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "AA test for WAC Refresh Q4 FY26",
            expectedReleaseDate: "Team26",
            expectedRuntime: "TBD",
            context: {
                urls: [
                    "/",
                    "/software/jira",
                    "/software/confluence",
                    "/software/rovo",
                    "/collections/teamwork"
                ]
            }
        }
    },
    softwareTryRovoCta: {
        experimentName: "try_rovo_cta",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedOut
        },
        metadata: {
            description: "Try rovo cta on /software page",
            expectedReleaseDate: "04/10/2026",
            expectedRuntime: "35 days",
            context: {
                urls: ["/software"]
            }
        }
    },
    gwpAJCBConfBPSv2: {
        experimentName: "gwp-aj-cb-conf-bps-v2",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                if (
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useSpecifiedPathnames(["/software/confluence"]) &&
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useLoggedOut() &&
                    // oxlint-disable-next-line react-hooks/rules-of-hooks
                    useEnglishLocale()
                ) {
                    return true;
                }
                return false;
            }
        },
        metadata: {
            description:
                "Contextual bandits for Confluence BPS v2 using sub anchor targeting",
            expectedReleaseDate: "04/10/2026",
            expectedRuntime: "10 weeks",
            context: {
                urls: ["/software/confluence/*"]
            }
        }
    },
    servCoLofiBackground: {
        experimentName: "gwp-9785-servco-lofi-background-signup",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Lofi background for servco signup pages",
            expectedReleaseDate: "04/30/2026",
            expectedRuntime: "4 weeks",
            context: {
                urls: [
                    "/try/cloud/signup?bundle=service-collection",
                    "/ondemand/signup/confirmation"
                ]
            }
        }
    },
    sparkSkipOnboardingWizard: {
        experimentName: "spark-jira-skip-onboarding-wizard",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "adding skip button to Jira onboarding wizard",
            expectedReleaseDate: "04/30/2026",
            expectedRuntime: "4 weeks",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfindSampleTasksForLandingGateNonTech: {
        experimentName: "jfind-sample-tasks-for-landing-gate-non-tech",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Provides sample task data during Jira onboarding for non-technical team types",
            expectedReleaseDate: "",
            expectedRuntime: "",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    twcg1935CodingAgentChoice: {
        experimentName: "spice-agentic-development-onboarding",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useEligibleForTwcg1935CodingAgentChoice
        },
        metadata: {
            description:
                "Show coding agent choice screen during Jira onboarding",
            expectedReleaseDate: "06/29/2026",
            expectedRuntime: "4 weeks",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    cfindTemplatesHeroIntentValueV6: {
        experimentName: "cfind_templates_hero_intent_value_v6",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Intent + Value Hero on Confluence templates page — optimize above-fold hero with intent-matched heading, value prop, social proof, and CTA",
            expectedReleaseDate: "06/15/2026",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/software/confluence/templates"]
            }
        }
    },
    jsmContextualBanditsInWae: {
        experimentName: "jsm-contextual-bandits-in-wae",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Contextual bandits in WAE on the JSM product page",
            expectedReleaseDate: "06/25/2026",
            expectedRuntime: "indefinite",
            context: {
                urls: ["/software/jira/service-management"]
            }
        }
    },
    wacRefreshJira,
    wacRefreshConfluence,
    wacRefreshHomepage,
    wacRefreshTwcRovo
} satisfies Manifest<unknown>;
